// ── Event log parser ──────────────────────────────────────────────────────────
export function parseLog(rows) {
  const hdr = rows[0] || [];
  const colVA = hdr.findIndex(h =>
    h && ["va/nva","vanva","va nva","classification"].includes(String(h).toLowerCase().trim())
  );
  const events = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const caseId = String(r[5] || '').trim();
    const step   = String(r[2] || '').trim();
    const evt    = String(r[4] || '').trim().toLowerCase();
    const res    = String(r[1] || '').trim();
    const vanva  = colVA >= 0 ? String(r[colVA] || '').trim().toUpperCase() : '';
    if (!caseId || !step || !evt) continue;
    let ts;
    try {
      const raw = r[3];
      ts = raw instanceof Date ? raw : new Date(String(raw).replace(' ', 'T'));
      if (isNaN(ts)) continue;
    } catch { continue; }
    events.push({ caseId, step, evt, res, ts, vanva });
  }
  events.sort((a, b) => a.caseId.localeCompare(b.caseId) || a.ts - b.ts);
  return events;
}

export function matchPairs(events, configCls = {}) {
  const stack = [], pairs = [];
  for (const e of events) {
    const key = `${e.caseId}|${e.step}`;
    if (e.evt === 'start' || e.evt === 'arrival') {
      stack.push({ key, ts: e.ts, res: e.res, step: e.step, caseId: e.caseId, cls: e.vanva || configCls[e.step] || '' });
    } else if (['complete','exit','departure'].includes(e.evt)) {
      const idx = [...stack].reverse().findIndex(p => p.key === key);
      if (idx >= 0) {
        const pi = stack.length - 1 - idx;
        const p  = stack[pi];
        const d  = (e.ts - p.ts) / 60000;
        if (d > 0) pairs.push({ ...p, endTs: e.ts, durMins: d });
        stack.splice(pi, 1);
      }
    }
  }
  return pairs;
}

export function arrStats(arr) {
  if (!arr.length) return { mean:0, median:0, p95:0, stddev:0, min:0, max:0, n:0 };
  const s = [...arr].sort((a,b)=>a-b), n = s.length;
  const mean = s.reduce((a,b)=>a+b,0)/n;
  const median = n%2 ? s[Math.floor(n/2)] : (s[n/2-1]+s[n/2])/2;
  const p95 = s[Math.min(Math.floor(n*0.95),n-1)];
  const stddev = Math.sqrt(s.reduce((a,b)=>a+(b-mean)**2,0)/n);
  return { mean, median, p95, stddev, min:s[0], max:s[n-1], n };
}

function cycleBounds(events) {
  const m = {};
  for (const e of events) {
    if (!m[e.caseId]) m[e.caseId] = { first:e.ts, last:e.ts };
    if (e.ts < m[e.caseId].first) m[e.caseId].first = e.ts;
    if (e.ts > m[e.caseId].last)  m[e.caseId].last  = e.ts;
  }
  return m;
}

export function computeCycleTime(events) {
  const cb = cycleBounds(events);
  const rows = Object.entries(cb)
    .map(([id,v]) => ({ caseId:id, cycleMins:(v.last-v.first)/60000 }))
    .sort((a,b)=>b.cycleMins-a.cycleMins);
  return { rows, stats: arrStats(rows.map(r=>r.cycleMins)) };
}

export function computePCE(events, configCls={}) {
  const pairs = matchPairs(events, configCls);
  const cb = cycleBounds(events);
  const va={}, bva={};
  for (const p of pairs) {
    if (!va[p.caseId])  va[p.caseId]=0;
    if (!bva[p.caseId]) bva[p.caseId]=0;
    if (p.cls==='VA')  va[p.caseId]  +=p.durMins;
    if (p.cls==='BVA') bva[p.caseId] +=p.durMins;
  }
  const rows = Object.entries(cb).map(([id,v])=>{
    const cycle=(v.last-v.first)/60000, vaT=va[id]||0, bvaT=bva[id]||0;
    return { caseId:id, cycleMins:cycle, va:vaT, bva:bvaT, nva:Math.max(0,cycle-vaT-bvaT), pce:cycle>0?(vaT+bvaT)/cycle*100:0 };
  }).sort((a,b)=>b.pce-a.pce);
  return { rows, stats: arrStats(rows.map(r=>r.pce)) };
}

export function computeVariants(events, configCls={}) {
  const pairs = matchPairs(events, configCls);
  const seq={}, cb=cycleBounds(events), va={}, bva={};
  for (const e of events) if (e.evt==='start'||e.evt==='arrival') { if(!seq[e.caseId]) seq[e.caseId]=[]; seq[e.caseId].push(`${e.step}(${e.evt})`); }
  for (const p of pairs) { if(!va[p.caseId]){va[p.caseId]=0;bva[p.caseId]=0;} if(p.cls==='VA') va[p.caseId]+=p.durMins; if(p.cls==='BVA') bva[p.caseId]+=p.durMins; }
  const vm={};
  for (const [cid,s] of Object.entries(seq)) {
    const key=s.join(' > '), cycle=((cb[cid]?.last||0)-(cb[cid]?.first||0))/60000;
    if(!vm[key]) vm[key]={count:0,tc:0,tva:0,tbva:0};
    vm[key].count++; vm[key].tc+=cycle; vm[key].tva+=va[cid]||0; vm[key].tbva+=bva[cid]||0;
  }
  const total=Object.values(vm).reduce((s,v)=>s+v.count,0);
  const rows=Object.entries(vm).map(([variant,v])=>{
    const ac=v.count>0?v.tc/v.count:0, av=v.count>0?v.tva/v.count:0, ab=v.count>0?v.tbva/v.count:0;
    return { variant, count:v.count, pct:total>0?+(v.count/total*100).toFixed(1):0, avgCycle:+ac.toFixed(1), avgVA:+av.toFixed(1), avgBVA:+ab.toFixed(1), avgNVA:+Math.max(0,ac-av-ab).toFixed(1), pce:+( ac>0?(av+ab)/ac*100:0).toFixed(1) };
  }).sort((a,b)=>b.count-a.count);
  return { rows, totalCases:total };
}

export function computeHandoffs(events) {
  const byCase={};
  for (const e of events) { if(e.evt!=='start'&&e.evt!=='arrival') continue; if(!byCase[e.caseId]) byCase[e.caseId]=[]; byCase[e.caseId].push(e); }
  const caseRows=[], pairMap={};
  for (const [cid,evs] of Object.entries(byCase)) {
    let ho=0, prev=null; const seq=[];
    for (const e of evs) { if(prev&&e.res!==prev){ho++;const pk=`${prev} > ${e.res} @ ${e.step}`;pairMap[pk]=(pairMap[pk]||0)+1;seq.push(`${prev}>${e.res}(${e.step})`);} prev=e.res; }
    caseRows.push({caseId:cid,handoffs:ho,sequence:seq.join(' | ')});
  }
  caseRows.sort((a,b)=>b.handoffs-a.handoffs);
  return { rows:caseRows, pairRows:Object.entries(pairMap).map(([pair,count])=>({pair,count})).sort((a,b)=>b.count-a.count), stats:arrStats(caseRows.map(r=>r.handoffs)), total:caseRows.reduce((s,r)=>s+r.handoffs,0) };
}

export function computeStepDurations(events, configCls={}) {
  const pairs=matchPairs(events,configCls), sm={};
  for (const p of pairs) { if(!sm[p.step]) sm[p.step]=[]; sm[p.step].push({caseId:p.caseId,durMins:p.durMins}); }
  const steps=Object.entries(sm).sort((a,b)=>a[0].localeCompare(b[0])).map(([step,obs])=>({step,obs,stats:arrStats(obs.map(o=>o.durMins))}));
  const allCases=[...new Set(pairs.map(p=>p.caseId))].sort();
  const pivot=allCases.map(cid=>{ const row={caseId:cid}; for(const st of steps){const ob=st.obs.find(o=>o.caseId===cid);row[st.step]=ob?+ob.durMins.toFixed(2):null;} return row; });
  return { steps, pivot, allCases };
}

export function computeResourceUtil(events, schedule={}) {
  // Schedule defaults: 5 days/week, 7.5 hours/day
  const { hoursPerDay=7.5, daysPerWeek=5 } = schedule;

  // Calculate working days in the observation window
  const timestamps = events.map(e => e.ts).filter(Boolean);
  const minTs = new Date(Math.min(...timestamps));
  const maxTs = new Date(Math.max(...timestamps));
  const totalDays = Math.max(1, (maxTs - minTs) / (1000 * 60 * 60 * 24));

  // Count actual working days (Mon-Fri) in the window
  let workingDays = 0;
  const cur = new Date(minTs);
  while (cur <= maxTs) {
    const dow = cur.getDay();
    if (dow >= 1 && dow <= 5) workingDays++;
    cur.setDate(cur.getDate() + 1);
  }
  workingDays = Math.max(1, workingDays);

  // Scale working days to the configured days per week
  const scaleFactor = daysPerWeek / 5;
  const effectiveWorkingDays = workingDays * scaleFactor;
  const schedHrsPerResource = effectiveWorkingDays * hoursPerDay;

  const pairs = matchPairs(events);
  const rm = {};
  for (const e of events) if (!rm[e.res]) rm[e.res] = { va:0, bva:0, nva:0, cases:new Set() };
  for (const e of events) rm[e.res].cases.add(e.caseId);
  for (const p of pairs) {
    if (!rm[p.res]) rm[p.res] = { va:0, bva:0, nva:0, cases:new Set() };
    const h = p.durMins / 60;
    if (p.cls==='VA') rm[p.res].va += h;
    else if (p.cls==='BVA') rm[p.res].bva += h;
    else rm[p.res].nva += h;
  }

  const rows = Object.entries(rm).map(([res, v]) => {
    const busy = v.va + v.bva + v.nva;
    const sched = schedHrsPerResource;
    const idle = Math.max(0, sched - busy);
    const util = sched > 0 ? (busy / sched) * 100 : 0;
    return {
      res, cases: v.cases.size,
      vaHrs: +v.va.toFixed(2), bvaHrs: +v.bva.toFixed(2), nvaHrs: +v.nva.toFixed(2),
      busyHrs: +busy.toFixed(2), schedHrs: +sched.toFixed(1),
      idleHrs: +idle.toFixed(2), util: +util.toFixed(1),
      vaPct: busy > 0 ? +(v.va / busy * 100).toFixed(1) : 0,
    };
  }).sort((a, b) => b.util - a.util);

  return { rows, workingDays, schedHrsPerResource:+schedHrsPerResource.toFixed(1), hoursPerDay, daysPerWeek };
}

export function buildRateCard(events) {
  const sys=["system","engine","bank","recon","platform","dtc","swift","euroclear","custodian"];
  const seen=new Set(), items=[];
  for (const e of events) { if(e.evt!=='start'&&e.evt!=='arrival') continue; const key=`${e.step}||${e.res}`; if(!seen.has(key)){seen.add(key);const isSys=sys.some(k=>e.res.toLowerCase().includes(k));items.push({step:e.step,res:e.res,costPerHour:isSys?0:75,type:isSys?'System':'Human'});} }
  return items.sort((a,b)=>a.step.localeCompare(b.step)||a.res.localeCompare(b.res));
}

export function computeABC(events, rateCard, configCls={}) {
  const pairs=matchPairs(events,configCls), cb=cycleBounds(events), rateMap={};
  for (const r of rateCard) rateMap[`${r.step}||${r.res}`]=r.costPerHour;
  const cm={};
  for (const e of events) if(!cm[e.caseId]) cm[e.caseId]={va:0,bva:0,nva:0,topAct:'',topCost:0};
  for (const p of pairs) { if(!cm[p.caseId]) continue; const rate=rateMap[`${p.step}||${p.res}`]??0; const cost=(p.durMins/60)*rate; if(p.cls==='VA') cm[p.caseId].va+=cost; else if(p.cls==='BVA') cm[p.caseId].bva+=cost; else cm[p.caseId].nva+=cost; if(cost>cm[p.caseId].topCost){cm[p.caseId].topCost=cost;cm[p.caseId].topAct=`${p.step}/${p.res}`;} }
  const rows=Object.entries(cm).map(([id,v])=>{ const total=v.va+v.bva+v.nva,cycle=((cb[id]?.last||0)-(cb[id]?.first||0))/60000; return {caseId:id,total,vaCost:v.va,bvaCost:v.bva,nvaCost:v.nva,vaPct:total>0?(v.va+v.bva)/total*100:0,cycleMins:cycle,costPerMin:cycle>0?total/cycle:0,topAct:v.topAct}; }).sort((a,b)=>b.total-a.total);
  return { rows, grandTotal:rows.reduce((s,r)=>s+r.total,0), stats:arrStats(rows.map(r=>r.total)) };
}

// ── Build analytics summary for API ──────────────────────────────────────────
export function buildAnalyticsSummary(events, rateCard = []) {
  const rc = rateCard.length ? rateCard : buildRateCard(events);
  const ct  = computeCycleTime(events);
  const pce = computePCE(events);
  const ho  = computeHandoffs(events);
  const sd  = computeStepDurations(events);
  const ru  = computeResourceUtil(events);

  const nCases = new Set(events.map(e=>e.caseId)).size;
  const nRes   = new Set(events.map(e=>e.res)).size;

  const pceRows = pce.rows;
  const totVA  = pceRows.reduce((s,r)=>s+r.va,0);
  const totBVA = pceRows.reduce((s,r)=>s+r.bva,0);
  const totNVA = pceRows.reduce((s,r)=>s+r.nva,0);
  const totAll = totVA+totBVA+totNVA;

  return {
    nCases, nEvents:events.length, nRes,
    nSteps: sd.steps.length,
    cycleTime: { mean:+ct.stats.mean.toFixed(0), median:+ct.stats.median.toFixed(0), p95:+ct.stats.p95.toFixed(0), min:+ct.stats.min.toFixed(0), max:+ct.stats.max.toFixed(0), unit:'minutes' },
    pce: { mean:+pce.stats.mean.toFixed(1), median:+pce.stats.median.toFixed(1) },
    vaNva: { va: totAll>0?+(totVA/totAll*100).toFixed(1):0, bva: totAll>0?+(totBVA/totAll*100).toFixed(1):0, nva: totAll>0?+(totNVA/totAll*100).toFixed(1):0 },
    handoffs: { mean:+ho.stats.mean.toFixed(1), max:ho.stats.max, total:ho.total },
    variants: { total:0, unique:0, topPct:0 },
    topSteps: sd.steps.sort((a,b)=>b.stats.mean-a.stats.mean).slice(0,8).map(s=>({step:s.step, mean:+s.stats.mean.toFixed(0), n:s.stats.n, p95:+s.stats.p95.toFixed(0)})),
    topRes: ru.rows.slice(0,6).map(r=>({res:r.res, busyHrs:+r.busyHrs.toFixed(1), util:+r.util.toFixed(0), cases:r.cases})),
    topHandoffPairs: ho.pairRows.slice(0,6).map(p=>({pair:p.pair, count:p.count})),
    abc: null,
  };
}

// ── Resource Activity Matrix ──────────────────────────────────────────────────
export function computeResourceActivityMatrix(events) {
  const pairs = matchPairs(events);

  // Collect all unique resources and steps
  const resources = [...new Set(pairs.map(p => p.res))].sort();
  const steps = [...new Set(pairs.map(p => p.step))].sort();

  // Build matrix: matrix[res][step] = { mins, count, va }
  const matrix = {};
  for (const res of resources) {
    matrix[res] = {};
    for (const step of steps) {
      matrix[res][step] = { mins: 0, count: 0, cls: '' };
    }
  }

  for (const p of pairs) {
    if (!matrix[p.res]) continue;
    if (!matrix[p.res][p.step]) matrix[p.res][p.step] = { mins: 0, count: 0, cls: '' };
    matrix[p.res][p.step].mins  += p.durMins;
    matrix[p.res][p.step].count += 1;
    if (!matrix[p.res][p.step].cls) matrix[p.res][p.step].cls = p.cls || '';
  }

  // Build row summaries
  const rows = resources.map(res => {
    const cells = steps.map(step => ({
      step,
      mins:  +matrix[res][step].mins.toFixed(1),
      hrs:   +(matrix[res][step].mins / 60).toFixed(2),
      count: matrix[res][step].count,
      cls:   matrix[res][step].cls,
    }));
    const totalMins  = cells.reduce((s, c) => s + c.mins, 0);
    const totalCount = cells.reduce((s, c) => s + c.count, 0);
    const uniqueSteps = cells.filter(c => c.count > 0).length;
    const vaMins  = cells.filter(c => c.cls === 'VA').reduce((s, c) => s + c.mins, 0);
    const bvaMins = cells.filter(c => c.cls === 'BVA').reduce((s, c) => s + c.mins, 0);
    const nvaMins = cells.filter(c => c.cls === 'NVA').reduce((s, c) => s + c.mins, 0);
    return { res, cells, totalMins: +totalMins.toFixed(1), totalHrs: +(totalMins/60).toFixed(2), totalCount, uniqueSteps, vaMins: +vaMins.toFixed(1), bvaMins: +bvaMins.toFixed(1), nvaMins: +nvaMins.toFixed(1) };
  }).sort((a, b) => b.totalMins - a.totalMins);

  // Column totals
  const colTotals = steps.map(step => {
    const total = resources.reduce((s, res) => s + (matrix[res][step]?.mins || 0), 0);
    const count = resources.reduce((s, res) => s + (matrix[res][step]?.count || 0), 0);
    const resCount = resources.filter(res => (matrix[res][step]?.count || 0) > 0).length;
    return { step, mins: +total.toFixed(1), hrs: +(total/60).toFixed(2), count, resCount };
  });

  return { rows, steps, colTotals, resources };
}

// ── Process Map (Directly-Follows Graph) ─────────────────────────────────────
export function computeProcessMap(events) {
  // Build transition counts between steps
  const byCase = {};
  for (const e of events) {
    if (e.evt !== 'start' && e.evt !== 'arrival') continue;
    if (!byCase[e.caseId]) byCase[e.caseId] = [];
    byCase[e.caseId].push(e);
  }

  const transitions = {}; // "A->B": count
  const nodeCounts  = {}; // "A": count
  const nodeVA      = {}; // "A": VA classification

  for (const evs of Object.values(byCase)) {
    evs.sort((a, b) => a.ts - b.ts);
    for (let i = 0; i < evs.length; i++) {
      const s = evs[i].step;
      nodeCounts[s] = (nodeCounts[s] || 0) + 1;
      if (!nodeVA[s]) nodeVA[s] = evs[i].vanva || '';
      if (i < evs.length - 1) {
        const t = evs[i + 1].step;
        const key = `${s}|||${t}`;
        transitions[key] = (transitions[key] || 0) + 1;
      }
    }
  }

  const totalCases = Object.keys(byCase).length;

  // Top transitions sorted by frequency
  const edges = Object.entries(transitions)
    .map(([key, count]) => {
      const [from, to] = key.split('|||');
      return { from, to, count, pct: +(count / totalCases * 100).toFixed(1) };
    })
    .sort((a, b) => b.count - a.count);

  // Nodes sorted by frequency
  const nodes = Object.entries(nodeCounts)
    .map(([step, count]) => ({ step, count, pct: +(count / totalCases * 100).toFixed(1), va: nodeVA[step] || '' }))
    .sort((a, b) => b.count - a.count);

  return { nodes, edges, totalCases };
}
